import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

export const ACTION_LABEL = { approve: 'Approve', reject: 'Reject', suspend: 'Suspend', reactivate: 'Reactivate' };
const DONE = { approve: 'approved', reject: 'rejected', suspend: 'suspended', reactivate: 'reactivated' };
const detailOf = (e) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join('; ') : d || 'Something went wrong. Please try again.';
};

// Approve / reject / suspend / reactivate a practice: reason + password + current authenticator code (fresh_auth on the server).
export const PracticeActionDialog = ({ target, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); setPassword(''); setCode(''); setError(''); }, [target]);
  const { o, action } = target || {};
  const override = action === 'approve' && o && !o.owner_email_verified;
  const needReason = ['reject', 'suspend'].includes(action) || override;
  const valid = (!needReason || reason.trim()) && password && /^\d{6}$/.test(code);
  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setError('');
    try {
      await api.post(`/api/platform/practices/${o.id}/${action}`, { reason: reason.trim() || null, password, mfa_code: code, override_unverified: !!override }, { skipAuthRedirect: true });
      toast.success(`${o.name} ${DONE[action]}`);
      onDone();
    } catch (err) { setError(detailOf(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="practice-action-dialog">
        {o && (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle data-testid="practice-action-title">{ACTION_LABEL[action]} {o.name}</DialogTitle>
              <DialogDescription>Re-confirm your password and the current code from your authenticator app. This action is audited.</DialogDescription>
            </DialogHeader>
            {override && <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800" data-testid="practice-action-override-note">
              {o.owner_email || 'The owner'} hasn&apos;t confirmed their email. Approving anyway is an override and needs a reason.</p>}
            <div className="space-y-1">
              <Label htmlFor="pa-reason">Reason{needReason ? '' : ' (optional)'}{['reject', 'suspend'].includes(action) ? ' – sent to the practice owner' : ''}</Label>
              <Textarea id="pa-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="practice-action-reason" />
              {needReason && !reason.trim() && <p className="text-xs text-muted-foreground" data-testid="practice-action-reason-required">A reason is required</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="pa-password">Your password</Label>
              <Input id="pa-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="practice-action-password" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pa-code">6-digit authenticator code</Label>
              <Input id="pa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} className="font-mono tracking-widest"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="practice-action-code" />
            </div>
            {error && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive" role="alert" data-testid="practice-action-error">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="practice-action-cancel">Cancel</Button>
              <Button type="submit" variant={action === 'reject' || action === 'suspend' ? 'destructive' : 'default'} disabled={!valid || busy} data-testid="practice-action-confirm">
                {busy ? 'Working…' : ACTION_LABEL[action]}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
